// src/components/context/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { api } from "../../utils/apiService";
import {
  getAccessToken,
  getUser,
  setAuthSession,
  clearAuth,
} from "../../utils/authStorage";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(getUser() || null);
  const [token, setToken] = useState(getAccessToken() || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [otp, setOtp] = useState([]);
  const otpRefs = React.createRef();

  // -------------------------
  // Helpers
  // -------------------------
  const saveSession = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    setAuthSession({ accessToken, user: userData });
  };

  const removeSession = useCallback(() => {
    setUser(null);
    setToken(null);
    clearAuth();
  }, []);

  // -------------------------
  // Fetch current user
  // -------------------------
  const fetchMe = useCallback(async () => {
    try {
      const currentToken = getAccessToken();
      if (!currentToken) {
        return { ok: false };
      }

      const res = await api.get("/auth/me");

      if (res.status === 200 && res.data?.user) {
        setUser(res.data.user);
        return { ok: true, user: res.data.user };
      }

      // Only remove session if we get an authentication error (401)
      return { ok: false };
    } catch (error) {
      // Only remove session on 401 Unauthorized, not on network errors
      if (error.response?.status === 401) {
        removeSession();
      }
      return { ok: false };
    }
  }, [removeSession]);

  useEffect(() => {
    const storedToken = getAccessToken();
    if (storedToken) {
      fetchMe();
    }
  }, [fetchMe]); // Run only once on mount

  // -------------------------
  // Auth Actions
  // -------------------------

  // Normal Login
  const login = async (email, password, otpMethod = null, code = null, targetPhone = null) => {
    if (!email || !password)
      return { ok: false, message: "Email and password required" };
    setLoading(true);
    setError(null);
    try {
      const payload = { email, password };
      if (otpMethod) payload.otpMethod = otpMethod;
      if (code) payload.code = code;
      if (targetPhone) payload.targetPhone = targetPhone;

      const res = await api.post("/auth/login", payload);

      const { data } = res;

      // New Stages: 2FA or One-time Verification
      if (data.requires2FA) {
        return { ok: true, requires2FA: true, email: data.email };
      }

      if (data.requiresVerification) {
        return {
          ok: true,
          requiresVerification: true,
          user: data.user,
        };
      }

      // Stage 1: Success - Fully logged in
      if (data.user && data.accessToken) {
        saveSession(data.user, data.accessToken);
        toast.success("Login successful");
        return { ok: true, user: data.user };
      }

      // Stage 2: Choice - OTP method selection required
      if (data.otpRequired) {
        return { ok: true, otpRequired: true, methods: data.methods };
      }

      // Stage 3: Sent - OTP has been dispatched
      if (data.otpSent) {
        toast.success(data.message || "OTP sent successfully");
        return { ok: true, otpSent: true, method: data.method };
      }

      throw new Error(data.message || "Login failed");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.message ||
        "Login failed";
      setError(msg);
      toast.error(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // OTP Login
  const loginWithOtp = async (identifier, otp, type = "email") => {
    if (!identifier || !otp)
      return { ok: false, message: "Email/Phone and OTP are required" };
    setLoading(true);
    setError(null);
    try {
      const payload = { otp };
      if (type === "phone") payload.phone = identifier;
      else payload.email = identifier;

      const res = await api.post("/auth/login/otp", payload);
      if (res.data?.user && res.data?.accessToken) {
        saveSession(res.data.user, res.data.accessToken);
        toast.success("Logged in via OTP");
        return { ok: true, user: res.data.user };
      }
      throw new Error(res.data?.message || "OTP login failed");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.message ||
        "OTP login failed";
      setError(msg);
      toast.error(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth Login
  const googleLogin = async (credential) => {
    if (!credential) return { ok: false, message: "Google credential missing" };
    setLoading(true);
    try {
      const res = await api.post("/auth/google/login", { token: credential });

      if (res.data?.requires2FA) {
        return { ok: true, requires2FA: true, email: res.data.user?.email };
      }

      if (res.data?.requiresVerification) {
        return {
          ok: true,
          requiresVerification: true,
          user: res.data.user,
        };
      }

      if (res.data?.user && res.data?.accessToken) {
        saveSession(res.data.user, res.data.accessToken);
        return { ok: true, user: res.data.user };
      }
      throw new Error(res.data?.message || "Google login failed");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.message ||
        "Google login failed";
      toast.error(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP Change
  const handleOtpChange = (e, index) => {
    const val = e.target.value.slice(-1);
    if (isNaN(val)) return;
    const newOtp = [...otp];
    newOtp[index] = val;
    setOtp(newOtp);
    if (val && index < 5) otpRefs.current[index + 1]?.focus();
  };

  // Register
  const register = async (username, email, password, otpCode, phone) => {
    if ((!email && !phone) || !password || !username) {
      return {
        ok: false,
        message: "Email/Phone, password, and username are required",
      };
    }

    if (!otpCode) {
      return { ok: false, message: "OTP verification is required" };
    }

    setLoading(true);
    setError(null);
    try {
      console.log("Attempting registration for:", email || phone);
      const payload = { password, username, otpCode };
      if (email) payload.email = email;
      if (phone) payload.phone = phone;

      const response = await api.post("/auth/register", payload);

      if (response.data?.user && response.data?.accessToken) {
        saveSession(response.data.user, response.data.accessToken);
        toast.success("Registration successful!");
        return { ok: true, user: response.data.user };
      }

      throw new Error(response.data?.message || "Registration failed");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.message ||
        "Registration failed";
      console.error("Registration error details:", err.response?.data || err);
      setError(msg);
      toast.error(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // Send OTP
  const sendOtp = async (identifier, purpose = "login", type = "email") => {
    if (!identifier) return { ok: false, message: "Identifier is required" };
    setLoading(true);
    setError(null);
    try {
      const payload =
        type === "phone"
          ? { phone: identifier, purpose }
          : { email: identifier, purpose };
      const res = await api.post("/auth/send-otp", payload);
      toast.success(res.data.message || `OTP sent to ${type}`);
      return { ok: true, data: res.data };
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.message ||
        "Failed to send OTP";
      setError(msg);
      toast.error(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP (Updated for Email/Phone)
  const verifyOtp = async (
    identifier,
    code,
    purpose = "login",
    type = "email",
  ) => {
    if (!identifier || !code) {
      const msg = "Identifier and OTP code are required";
      toast.error(msg);
      return { ok: false, message: msg };
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        [type]:
          type === "email"
            ? identifier.toLowerCase().trim()
            : identifier.trim(),
        code: code.toString().trim(),
        purpose,
      };

      console.log("Sending OTP verification request...", payload);
      const res = await api.post("/auth/verify-otp", payload);

      console.log("OTP verification response:", res.data);

      if (res.data?.success) {
        toast.success(res.data.message || "OTP verified successfully");

        // If auto-login tokens are returned (post-OAuth or direct verification login)
        if (res.data.user && res.data.accessToken) {
          saveSession(res.data.user, res.data.accessToken);
        }

        return {
          ok: true,
          data: res.data.data,
          user: res.data.user,
          accessToken: res.data.accessToken,
          message: res.data.message,
        };
      }

      throw new Error(res.data?.message || "OTP verification failed");
    } catch (err) {
      console.error("OTP verification error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.message ||
        "Failed to verify OTP. Please try again.";

      setError(errorMessage);
      toast.error(errorMessage);

      return {
        ok: false,
        message: errorMessage,
        error: err.response?.data,
      };
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const resendOtp = async (email, purpose = "login") => sendOtp(email, purpose);

  // Forgot Password
  const forgotPassword = async (email) => {
    if (!email) return { ok: false, message: "Email is required" };
    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      toast.success(res.data.message || "Password reset link sent");
      return { ok: true };
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to send reset link";
      toast.error(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // Reset Password
  const resetPassword = async (tokenParam, newPassword) => {
    if (!tokenParam || !newPassword)
      return { ok: false, message: "Token and new password required" };
    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/auth/reset-password", {
        token: tokenParam,
        newPassword,
      });
      toast.success(res.data.message || "Password reset successful");
      return { ok: true };
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to reset password";
      toast.error(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // Verify 2FA Login
  const verify2FALogin = async (email, token) => {
    if (!email || !token)
      return { ok: false, message: "Email and TOTP token are required" };
    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/auth/2fa/login", { email, token });
      if (res.data?.user && res.data?.accessToken) {
        saveSession(res.data.user, res.data.accessToken);
        toast.success("MFA Verification Successful");
        return { ok: true, user: res.data.user };
      }
      throw new Error(res.data?.message || "2FA verification failed");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.message ||
        "2FA verification failed";
      setError(msg);
      toast.error(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // Verify Sudo Mode
  const verifySudo = async (password) => {
    if (!password) return { ok: false, message: "Password is required" };
    setLoading(true);
    try {
      const res = await api.post("/auth/sudo", { password });

      if (res.data?.success && res.data?.sudoToken) {
        // Store sudo token in session storage or state as needed
        // For this implementation, we return it to the caller to be used in the header
        toast.success("Identity verified");
        return { ok: true, sudoToken: res.data.sudoToken };
      }
      throw new Error(res.data?.message || "Verification failed");
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Verification failed";
      toast.error(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to logout";
      toast.error(msg);
    }
    removeSession();
    navigate("/login");
    toast.info("Logged out");
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user,
    loading,
    error,
    otp,
    fetchMe,
    login,
    loginWithOtp,
    googleLogin,
    verify2FALogin,
    register,
    sendOtp,
    verifyOtp,
    resendOtp,
    forgotPassword,
    resetPassword,
    verifySudo,
    logout,
    handleOtpChange,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
